import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getPhotosByProject, deletePhoto } from '../services/photoService';
import { enqueuePhotoUploads } from '../services/uploadQueue';
import { getProject } from '../services/projectService';
import { Photo, Project } from '../types';
import { validateImageFile } from '../utils/imageFormatConverter';
import CameraComponent from '../components/CameraComponent';
import PhotoList from '../components/PhotoList';
import { PageShell } from '../components/ui/PageShell';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { FloatingActionButton } from '../components/ui/FloatingActionButton';
import { useFullScreen } from '../components/Layout';

const PhotoListPage: React.FC = () => {
  const { setIsFullScreen } = useFullScreen();
  const { projectId } = useParams<{ projectId: string }>();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  useEffect(() => {
    if (!projectId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedPhotos, fetchedProject] = await Promise.all([
          getPhotosByProject(projectId),
          getProject(projectId)
        ]);
        setPhotos(fetchedPhotos);
        setProject(fetchedProject);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte ladda data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  const refreshPhotos = async () => {
    if (!projectId) return;
    try {
      setPhotos(await getPhotosByProject(projectId));
    } catch (err) {
      console.error('Error refreshing photos:', err);
    }
  };

  // Fires per saved photo while the camera stays open for the next shot.
  const handlePhotoTaken = (photoId: string) => {
    refreshPhotos();
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    refreshPhotos();
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!projectId || !photoId) return;

    try {
      setDeleteLoading(photoId);
      await deletePhoto(photoId, projectId);
      setPhotos(photos.filter(photo => photo.id !== photoId));
      setShowConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Kunde inte ta bort fotot. Försök igen senare.');
      setTimeout(() => setError(null), 3000);
      setShowConfirmDelete(null);
    } finally {
      setDeleteLoading(null);
    }
  };

  // Library upload from the nav bar: validate, hand everything to the
  // background queue, and let the grid refresh as each photo lands. Nothing
  // here blocks — the user can leave the page and the uploads carry on.
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = ''; // allow picking the same files again
    if (!projectId || files.length === 0) return;

    const valid: File[] = [];
    const rejected: string[] = [];
    files.forEach(file => {
      const check = validateImageFile(file);
      if (check.valid) valid.push(file);
      else rejected.push(`${file.name}: ${check.error}`);
    });

    if (rejected.length > 0) {
      setUploadError(`Vissa bilder hoppades över: ${rejected.join(', ')}`);
      setTimeout(() => setUploadError(null), 6000);
    }
    if (valid.length === 0) return;

    enqueuePhotoUploads(valid, { projectId }, { onUploaded: () => refreshPhotos() });
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  if (showCamera) {
    return (
      <div className="relative pb-28">
        <CameraComponent
          projectId={projectId || ''}
          onPhotoTaken={handlePhotoTaken}
          onClose={handleCameraClose}
        />
      </div>
    );
  }

  return (
    <PageShell
      title={`Foton${!loading ? ` (${photos.length})` : ''}`}
      backTo={`/projects/${projectId}`}
      backLabel="Projekt"
      noPadding
      headerRight={
        <button
          onClick={triggerFileUpload}
          className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity min-h-[44px] flex items-center"
        >
          Ladda upp
        </button>
      }
    >
      {/* Loading */}
      {loading && (
        <div className="px-4 py-4">
          <SkeletonGrid count={8} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mx-4 mt-4">
          {error}
        </div>
      )}

      {uploadError && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-amber-800 dark:text-amber-300 px-4 py-3 rounded-xl text-sm mx-4 mt-4" role="alert">
          {uploadError}
        </div>
      )}

      {/* Photo Grid */}
      {!loading && !error && (
        <div className="pb-28 max-w-5xl mx-auto w-full">
          <PhotoList
            photos={photos}
            projectId={projectId || ''}
            contextLabel={project?.address?.street || project?.contact?.name}
            onPhotoDeleted={(photoId) => {
              setPhotos(photos.filter(p => p.id !== photoId));
            }}
            onAddPhotoClick={() => setShowCamera(true)}
            showAddButton={false}
            setPhotos={setPhotos}
            onSelectionModeChange={setIsSelecting}
          />
        </div>
      )}

      {/* Floating Action Button — hidden during selection mode */}
      {!loading && !isSelecting && (
        <>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*,.heic,.heif,.avif"
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* One tap to the viewfinder; album upload lives in the nav bar. */}
          <FloatingActionButton
            onClick={() => setShowCamera(true)}
            aria-label="Ta foto"
          />
        </>
      )}
    </PageShell>
  );
};

export default PhotoListPage;
